import React, { useState, useCallback, useRef, useEffect } from "react";
import styles from "./SignatureUploadModal.module.scss";
import ReactModalWrap from "../UI/Modals/ReactModalWrap/ReactModalWrap";
import Button from "../UI/Buttons/Button/Button";
import { uploadUserSignature } from "../../services/userSignatureService";

interface Props {
  isOpen: boolean;
  userId: number | null;
  onClose: () => void;
  onSuccess: () => void;
}

const SignatureUploadModal: React.FC<Props> = ({
  isOpen,
  userId,
  onClose,
  onSuccess,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedFile(null);
      setPreviewUrl(null);
      setError(null);
      setIsUploading(false);
    }
  }, [isOpen]);

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      // Validate file type
      const validTypes = ["image/png", "image/jpeg", "image/jpg"];
      if (!validTypes.includes(file.type)) {
        setError("Molimo izaberite PNG ili JPEG sliku.");
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError("Slika je prevelika. Maksimalna veličina je 5MB.");
        return;
      }

      setError(null);
      setSelectedFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    },
    []
  );

  const handleUpload = useCallback(async () => {
    if (!selectedFile || !userId) return;

    setIsUploading(true);
    setError(null);

    try {
      await uploadUserSignature(userId, selectedFile);
      onSuccess();
    } catch (err: any) {
      console.error("Signature upload failed:", err);
      const errorMessage =
        err.response?.status === 500
          ? "Greška pri obradi slike. Molimo pokušajte sa drugom slikom."
          : "Došlo je do greške. Molimo pokušajte ponovo.";
      setError(errorMessage);
    } finally {
      setIsUploading(false);
    }
  }, [selectedFile, userId, onSuccess]);

  const handleRemovePreview = useCallback(() => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);

  const handleBrowseClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  return (
    <ReactModalWrap isOpen={isOpen} close={onClose}>
      <div className={styles.modalContent}>
        <div className={styles.header}>
          <h2>Potreban digitalni potpis</h2>
          <p className={styles.subtitle}>
            Kako bismo kreirali ovlašćenje da preuzmeš rezervaciju u ime drugog
            člana, potrebno je da dodaš svoj digitalni potpis.
          </p>
        </div>

        <div className={styles.uploadArea}>
          {previewUrl ? (
            <div className={styles.previewContainer}>
              <img
                src={previewUrl}
                alt="Pregled potpisa"
                className={styles.preview}
              />
              <button
                type="button"
                className={styles.removeButton}
                onClick={handleRemovePreview}
              >
                ✕ Ukloni
              </button>
            </div>
          ) : (
            <div className={styles.dropzone} onClick={handleBrowseClick}>
              <div className={styles.dropzoneContent}>
                <span className={styles.uploadIcon}>↑</span>
                <span>Klikni da izabereš fotografiju svog potpisa.</span>
              </div>
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/jpg"
            onChange={handleFileSelect}
            className={styles.hiddenInput}
          />
        </div>

        <div className={styles.note}>
          <span className={styles.noteIcon}>ℹ</span>
          <span>
            Fotografija treba da bude snimljena na beloj pozadini, tako da
            potpis bude centriran. Svoj potpis možeš promeniti u okviru stranice
            Moj profil ili putem aplikacije apoteka.rs.
          </span>
        </div>

        {error && <div className={styles.error}>{error}</div>}

        <div className={styles.actions}>
          <Button buttonType="white" onClick={onClose} disabled={isUploading}>
            Odustani
          </Button>
          <Button
            buttonType="blue"
            onClick={handleUpload}
            disabled={!selectedFile || isUploading}
          >
            {isUploading ? "Učitavanje..." : "Sačuvaj potpis"}
          </Button>
        </div>
      </div>
    </ReactModalWrap>
  );
};

export default SignatureUploadModal;
